<template>
  <div class="terms-page">
    <section class="header-section">
      <div class="section-container">
        <h1 class="page-title">{{ $t('pages.terms.title') }}</h1>
        <p class="page-subtitle">
          {{ $t('pages.terms.subtitle') }}
        </p>
      </div>
    </section>

    <section class="content-section">
      <div class="section-container">
        <div class="content-card terms-content">
          <div v-html="termsHtml"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import zhHtml from '../../assets/legal/terms/zh.html?raw'
import enHtml from '../../assets/legal/terms/en.html?raw'

const { t, locale } = useI18n()
useWebsiteSeo({
  title: () => t('seo.pages.terms.title'),
  description: () => t('seo.pages.terms.desc')
})

const normalizeLang = (l: string) => ['zh', 'zh-CN', 'zh_CN', 'zh-Hans', 'zh-Hans-CN'].includes(l) ? 'zh' : 'en'
const lang = computed(() => normalizeLang(locale.value))
const termsHtml = computed(() => (lang.value === 'zh' ? zhHtml : (enHtml || zhHtml)))
</script>

<style scoped lang="scss">
@use 'sass:color';

.terms-page {
  min-height: 100vh;
  background: $gray-50;
}

.header-section {
  background: linear-gradient(135deg, $primary-color 0%, $primary-dark 100%);
  color: $white;
  padding: $spacing-xxl 0;
  text-align: center;

  .page-title {
    font-size: 3rem;
    font-weight: 700;
    margin: 0 0 $spacing-md 0;
  }

  .page-subtitle {
    font-size: 1.2rem;
    margin: 0;
    opacity: 0.9;
  }
}

  .content-section {
    padding: $spacing-xxl 0;

  .section-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 $spacing-lg;
  }

    .content-card {
      background: $white;
      padding: $spacing-xxl;
      border-radius: $border-radius-lg;
      box-shadow: $shadow-lg;

    h2 {
      color: $primary-color;
      margin: $spacing-xl 0 $spacing-md 0;
      font-size: 1.3rem;

      &:first-child {
        margin-top: 0;
      }
    }

    p {
      line-height: 1.6;
      color: $gray-700;
      margin-bottom: $spacing-md;
    }

    ul {
      margin: $spacing-md 0;
      padding-left: $spacing-lg;

      li {
        line-height: 1.6;
        color: $gray-700;
        margin-bottom: $spacing-xs;
      }
    }

    .update-date {
      margin-top: $spacing-xl;
      font-size: 0.9rem;
      color: $gray-500;
      text-align: center;
      border-top: 1px solid $gray-200;
      padding-top: $spacing-md;
    }

    /* Enhance default styles for terms HTML */
    .terms-content {
      h1 {
        font-size: 1.8rem;
        margin: 0 0 $spacing-md 0;
        color: $gray-900;
      }

      h2 {
        margin-top: $spacing-xl;
      }

      a {
        color: $primary-color;
        text-decoration: underline;
      }

      dl {
        margin: $spacing-md 0;
      }
      dt {
        font-weight: 600;
        color: $gray-800;
      }
      dd {
        margin: 0 0 $spacing-xs $spacing-md;
        color: $gray-700;
      }

      strong {
        font-weight: 600;
        color: $gray-800;
      }
    }
  }
}

@media (max-width: 768px) {
  .header-section {
    padding: $spacing-xl 0;

    .page-title {
      font-size: 2rem;
    }
  }

  .content-section {
    .content-card {
      padding: $spacing-lg;
    }
  }
}
</style>
